import { excludeUndefinedDataKeys, IS_CLIENT } from '@repo/common-utils';
import { useUTM } from '@repo/platform';
import { format, isWithinInterval } from 'date-fns';
import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useEffect, useMemo, useRef, useState } from 'react';

export const MODEL_HOYEONG_LAB_TYPE = 'model_hoyeong';
export const MODEL_WONIL_LAB_TYPE = 'model_wonil';
export const BOOSTING_LAB_TYPE = 'boosting_v5';

type LabTypes = typeof MODEL_HOYEONG_LAB_TYPE | typeof MODEL_WONIL_LAB_TYPE | typeof BOOSTING_LAB_TYPE;
type LabIds = 'control' | 'experiment';

type SectionScrollModelContextType = {
  labId: LabIds | null;
  labType: LabTypes | null;
  screenType: 'mobile' | 'pc';
  currentSectionId: string;
  setCurrentSectionId: Dispatch<SetStateAction<string>>;
  scrollDirection: 'up' | 'down';
  eventAttribute?: Record<string, string>;
};

const SectionScrollModelContext = createContext<SectionScrollModelContextType>({
  labId: null,
  labType: null,
  screenType: 'pc',
  currentSectionId: '01',
  scrollDirection: 'down',
  setCurrentSectionId: () => {}
});

const useLabAttributes = () => {
  const { utmData } = useUTM();
  const isQaTestEnv = ['dev', 'loc'].includes(process.env.NEXT_PUBLIC_ZENV || '');

  const STORAGE_KEY = 'zent_landing_test_data';
  const labSettings = useMemo<Record<LabTypes, { startDate: Date; endDate: Date; experimentRatio: number }>>(
    () => ({
      [BOOSTING_LAB_TYPE]: {
        startDate: new Date('2026-01-19T00:00:00+09:00'),
        endDate: new Date('2026-01-31T23:59:59+09:00'),
        experimentRatio: 0.5
      },
      [MODEL_WONIL_LAB_TYPE]: {
        startDate: new Date('2025-10-29T00:00:00+09:00'),
        endDate: new Date(isQaTestEnv ? '2026-02-11T23:59:59+09:00' : '2026-02-27T23:59:59+09:00'),
        experimentRatio: 1.0
      },
      [MODEL_HOYEONG_LAB_TYPE]: {
        startDate: new Date(isQaTestEnv ? '2026-02-12T00:00:00+09:00' : '2026-03-03T00:00:00+09:00'),
        endDate: new Date('2026-06-02T23:59:59+09:00'),
        experimentRatio: 0.5
      }
    }),
    [isQaTestEnv]
  );

  const currentLabType = useMemo<LabTypes | null>(() => {
    const now = Date.now();
    if (isWithinInterval(now, { start: labSettings[BOOSTING_LAB_TYPE].startDate, end: labSettings[BOOSTING_LAB_TYPE].endDate })) {
      return BOOSTING_LAB_TYPE;
    } else if (isWithinInterval(now, { start: labSettings[MODEL_WONIL_LAB_TYPE].startDate, end: labSettings[MODEL_WONIL_LAB_TYPE].endDate })) {
      return MODEL_WONIL_LAB_TYPE;
    } else if (isWithinInterval(now, { start: labSettings[MODEL_HOYEONG_LAB_TYPE].startDate, end: labSettings[MODEL_HOYEONG_LAB_TYPE].endDate })) {
      return MODEL_HOYEONG_LAB_TYPE;
    }
    return null;
  }, [labSettings]);

  if (IS_CLIENT && currentLabType) {
    const excludedUtmMedium = 'partner';
    const CURRENT_TEST_KEY = `test-${format(labSettings[currentLabType].startDate, 'yyyyMMdd')}-landing`;

    if (excludedUtmMedium === utmData?.utmMedium) {
      localStorage.removeItem(STORAGE_KEY);
      return {};
    }

    const savedTestData = localStorage.getItem(STORAGE_KEY);
    if (savedTestData) {
      try {
        const data = JSON.parse(savedTestData);
        // testKey가 현재 테스트와 동일하고, 저장된 id 값이 유효한 그룹이라면 그대로 사용
        if (data.testKey === CURRENT_TEST_KEY && ['control', 'experiment'].includes(data.labId)) {
          return { 'refund.lab.type': currentLabType, 'refund.lab.id': data.labId };
        }
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        localStorage.removeItem(STORAGE_KEY);
      }
    }

    const randomValue = Math.random();
    const labId: LabIds = randomValue < labSettings[currentLabType].experimentRatio ? 'experiment' : 'control';

    const newTestData = { labId, testKey: CURRENT_TEST_KEY };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newTestData));
    return { 'refund.lab.type': currentLabType, 'refund.lab.id': labId };
  } else if (IS_CLIENT) {
    localStorage.removeItem(STORAGE_KEY);
  }
  return {};
};

export const SectionScrollModelProvider = ({ children }: { children: ReactNode }) => {
  const [currentSectionId, setCurrentSectionId] = useState<string>('01');
  const [screenType, setScreenType] = useState<'mobile' | 'pc'>('pc');
  const previousScrollY = useRef<number>(0);
  const [scrollDirection, setScrollDirection] = useState<SectionScrollModelContextType['scrollDirection']>('down');

  const labAttributes = useLabAttributes();

  const eventAttribute: Record<string, string> = excludeUndefinedDataKeys({
    ...labAttributes,
    current_section: currentSectionId
  });

  const controlDirection = () => {
    const currentScrollY = window.scrollY;
    setScrollDirection(currentScrollY > previousScrollY.current ? 'down' : 'up');
    previousScrollY.current = currentScrollY;
  };

  useEffect(() => {
    window.addEventListener('scroll', controlDirection);
    return () => {
      window.removeEventListener('scroll', controlDirection);
    };
  }, []);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(([entry]) => {
      if (!entry) return;

      const fullWidth = entry.contentRect.width;

      setScreenType(fullWidth < 700 ? 'mobile' : 'pc');
    });

    resizeObserver.observe(document.body);

    return () => {
      resizeObserver.unobserve(document.body);
    };
  }, [screenType]);

  return (
    <SectionScrollModelContext.Provider
      value={{
        screenType,
        currentSectionId,
        setCurrentSectionId,
        eventAttribute,
        scrollDirection,
        labType: (eventAttribute['refund.lab.type'] as LabTypes) || null,
        labId: (eventAttribute['refund.lab.id'] as LabIds) || null
      }}
    >
      {children}
    </SectionScrollModelContext.Provider>
  );
};

export const useSectionScrollModel = () => {
  return useContext(SectionScrollModelContext);
};
